import { carimboDaMensagem, type MensagemChat } from '@mymaxx/shared';
import { Anexo, Citacao } from '@mymaxx/ui';
import { Check, CheckCheck, StickyNote } from 'lucide-react';

/**
 * Uma mensagem no fio, do jeito que a equipe lê.
 *
 * Mora fora da tela de atendimento porque o mesmo fio aparece em três lugares: no chat de
 * quem atende, na conversa encerrada da aba Atendimentos e no Monitor, quando o supervisor
 * abre um atendimento de alguém. Ler a mesma conversa de dois jeitos diferentes — bolha num
 * lugar, lista de "Fulano: texto" no outro — fazia a mídia sumir justamente em quem só
 * observa, que é quem mais precisa entender o que houve.
 *
 * Sem `onResponder` (leitura), a bolha não mostra o botão de responder nem os tiquinhos de
 * entrega: são controles de quem está dentro da conversa.
 */
export function Bolha({
  mensagem,
  onResponder,
}: {
  mensagem: MensagemChat;
  onResponder?: () => void;
}) {
  if (mensagem.de === 'sistema') {
    return (
      <p className="mx-auto max-w-[80%] rounded bg-neutral-200 px-2 py-1 text-center text-[11px] text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
        {mensagem.texto}
      </p>
    );
  }
  if (mensagem.tipo === 'nota_interna') {
    return (
      <div className="ml-auto max-w-[75%] rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-800 dark:bg-amber-950/40">
        <p className="mb-0.5 flex items-center gap-1 text-[11px] font-medium text-amber-800 dark:text-amber-300">
          <StickyNote size={11} /> Nota interna · {mensagem.autorNome}
        </p>
        {mensagem.texto}
      </div>
    );
  }

  const doCliente = mensagem.de === 'cliente';
  return (
    <div
      className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-line shadow-sm ${
        doCliente
          ? 'mr-auto rounded-bl-sm bg-white dark:bg-neutral-800'
          : 'ml-auto rounded-br-sm bg-marca-600 text-white'
      }`}
    >
      {!doCliente && mensagem.autorNome && (
        <p className="mb-0.5 text-[11px] opacity-70">{mensagem.autorNome}</p>
      )}
      {mensagem.respondeA && (
        <Citacao
          autor={
            mensagem.respondeA.de === 'cliente'
              ? 'Cliente'
              : (mensagem.respondeA.autorNome ?? 'Atendimento')
          }
          resumo={mensagem.respondeA.resumo}
        />
      )}
      {mensagem.midia && (
        <Anexo
          url={mensagem.midia.url}
          nomeOriginal={mensagem.midia.nomeOriginal}
          mimeType={mensagem.midia.mimeType}
          tamanho={mensagem.midia.tamanho}
          previaUrl={mensagem.midia.previaUrl}
        />
      )}
      {mensagem.texto}
      <span className="ml-2 inline-flex items-center gap-1 align-bottom text-[10px] opacity-60">
        {carimboDaMensagem(mensagem.criadaEm)}
        {onResponder && (
          <>
            {!doCliente && (mensagem.lidaEm ? <CheckCheck size={11} /> : <Check size={11} />)}
            <button type="button" onClick={onResponder} className="underline">
              Responder
            </button>
          </>
        )}
      </span>
    </div>
  );
}

/**
 * O fio inteiro, só para ler: fundo de chat, rolagem própria, mídia como mídia.
 *
 * Quem usa dá a altura (é sempre uma coluna dentro de outra coisa) — daí o `min-h-0`, sem
 * o qual um filho rolável dentro de um flex cresce em vez de rolar.
 */
export function FioDeLeitura({
  mensagens,
  vazio = 'Sem mensagens.',
  carregando = false,
}: {
  mensagens: MensagemChat[];
  vazio?: string;
  carregando?: boolean;
}) {
  if (carregando) {
    return <p className="p-4 text-sm text-neutral-500">Carregando conversa…</p>;
  }
  if (mensagens.length === 0) {
    return <p className="p-4 text-sm text-neutral-500">{vazio}</p>;
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto bg-neutral-50 px-3 py-3 dark:bg-neutral-900">
      {mensagens.map((m) => (
        <Bolha key={m.id} mensagem={m} />
      ))}
    </div>
  );
}
